/* Yeshuall: optional mobile/desktop art direction for custom section image pickers.
 * Desktop media stays as in V3. Mobile images are only substituted when provided.
 * Hero and image-banner are handled natively with <picture> to avoid duplicate downloads.
 */
(() => {
  const threshold = window.matchMedia('(max-width: 749px)');
  const original = new WeakMap();
  const selector = 'img,source,video,[style],[data-bg],[data-background],[data-src],[data-srcset],[data-image],style';
  const attributes = ['src','srcset','poster','style','data-bg','data-background','data-src','data-srcset','data-image'];
  const parse = (value) => {
    try { return new URL(value, document.baseURI); } catch (error) { return null; }
  };
  const makePair = (pair) => {
    const desktop = parse(pair.desktop), mobile = parse(pair.mobile);
    if (!desktop || !mobile) return null;
    return { desktop, mobile };
  };
  const replaceUrls = (value, mappings) => {
    // Shopify CDN: query parameters (width/crop) are carried over to the mobile file.
    // The file-version parameter is taken from the mobile file itself.
    return value.replace(/(?:https?:)?\/\/[^\s\"'()<>;,]+|\/cdn\/shop\/[^\s\"'()<>;,]+/g, (piece) => {
      const source = parse(piece);
      if (!source) return piece;
      const pair = mappings.find((entry) => source.pathname === entry.desktop.pathname);
      if (!pair) return piece;
      const target = new URL(pair.mobile.href);
      for (const [key, entry] of source.searchParams.entries()) {
        if (key !== 'v') target.searchParams.set(key, entry);
      }
      return target.href;
    });
  };
  const readPairs = (node) => {
    if (!node) return [];
    try {
      const data = JSON.parse(node.textContent);
      return Array.isArray(data) ? data.map(makePair).filter(Boolean) : [];
    } catch (error) { return []; }
  };
  const globalPairs = () => readPairs(document.getElementById('ys-global-art-direction'));
  const apply = (scope, mappings, mobile) => {
    if (!mappings.length) return;
    const nodes = scope.querySelectorAll(selector);
    for (const element of nodes) {
      if (element.closest('picture') && element.closest('picture').querySelector('source[media="(max-width: 749px)"]')) continue;
      if (element.tagName === 'STYLE') {
        const previous = original.get(element) || { text: element.textContent };
        if (!original.has(element)) original.set(element,previous);
        const replacement = mobile ? replaceUrls(previous.text,mappings) : previous.text;
        if (element.textContent !== replacement) element.textContent=replacement;
        continue;
      }
      let values = original.get(element);
      if (!values) {
        values = {};
        for (const attr of attributes) if (element.hasAttribute(attr)) values[attr]=element.getAttribute(attr);
        original.set(element,values);
      }
      // Avoid image srcset winning over src: rewrite both.
      for (const [name,before] of Object.entries(values)) {
        const after=mobile ? replaceUrls(before,mappings) : before;
        if (element.getAttribute(name)!==after) element.setAttribute(name,after);
      }
    }
  };
  const refresh = () => {
    const mobile = threshold.matches;
    const global = globalPairs();
    // Global logo and brand image are available throughout the site.
    if (global.length) apply(document.body,global,mobile);
    for (const data of document.querySelectorAll('script.ys-art-direction-data')) {
      const section = data.closest('[id^="shopify-section-"]');
      if (!section) continue;
      apply(section,readPairs(data),mobile);
    }
  };
  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(() => {pending=false;refresh();});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',refresh,{once:true});
  else refresh();
  if (threshold.addEventListener) threshold.addEventListener('change',schedule);
  else threshold.addListener(schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:block:select', schedule);
  // Slider/apps can inject media after initial section render.
  const observer = new MutationObserver((events) => {
    if (events.some(event => event.type==='childList' && [...event.addedNodes].some(n => n.nodeType===1 && n.tagName!=='STYLE'))) schedule();
  });
  const watch = () => observer.observe(document.body,{subtree:true,childList:true});
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',watch,{once:true});
  else watch();
})();
